// The thirty-second read: why admitted, how they are now, what changed,
// what is pending.
//
// Every number here is drilled from a real record, and every derived claim
// ("SpO2 improved from 91%") is computed from two observations the backend
// returned side by side — never inferred, never rounded, never AI-generated.
// The AI gist is a separate, collapsed card below this one.
//
// Signal over volume: this deliberately shows the LATEST vitals plus what
// moved, the ACTIVE orders, the UNREVIEWED results and the OPEN items — not
// the full history of any of them. History is a tab away.
import { Badge } from '@/components/ui/Badge';
import type { CaseSheet, SourceRef, Vitals } from './types';
import { Metric, Section, fmtAgo, SourceLink } from './ui';

/** A vital plus its direction of travel, when a comparison point exists. */
function VitalTrend({ label, unit, latest, prior, invert }: {
  label: string; unit: string;
  latest: number | null | undefined; prior: number | null | undefined;
  /** true when DOWN is the improvement (temperature, RR, pain). */
  invert?: boolean;
}) {
  if (latest === null || latest === undefined) return null;
  const l = Number(latest);
  const p = prior === null || prior === undefined ? null : Number(prior);
  let arrow = '';
  let tone = '';
  if (p !== null && Number.isFinite(p) && p !== l) {
    const rising = l > p;
    arrow = rising ? '↑' : '↓';
    const better = invert ? !rising : rising;
    tone = better ? 'text-success-ink' : 'text-warn-ink';
  }
  return (
    <div className="min-w-[78px]">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="text-sm font-semibold tabular-nums">
        {l}{unit}
        {arrow && (
          <span className={`ml-1 text-[11px] font-medium ${tone}`}>
            {arrow} from {p}{unit}
          </span>
        )}
      </div>
    </div>
  );
}

function oxygenLine(v: Vitals | null): string | null {
  if (!v || !v.oxygen_delivery) return null;
  if (v.oxygen_delivery === 'room-air') return 'Room air';
  const flow = v.oxygen_flow_lpm ? ` ${v.oxygen_flow_lpm} L/min` : '';
  return `${v.oxygen_delivery}${flow}`;
}

export function ClinicalSnapshot({ data, onOpen }: { data: CaseSheet; onOpen?: (r: SourceRef) => void }) {
  const v = data.latest_vitals;
  const p = data.prior_vitals;

  const unreviewedCritical = data.investigations.filter(i => i.is_critical && !i.reviewed_at);
  const pendingResults = data.investigations.filter(i => i.result_status === 'pending' && i.status !== 'cancelled');
  const openConsults = data.consults.filter(c => ['requested', 'accepted', 'in-progress'].includes(c.status));
  const openCare = data.care_plan.filter(a => a.status === 'planned' || a.status === 'in-progress');
  const heldOrders = data.medication_orders.filter(o => o.status === 'held');
  const o2 = oxygenLine(v);
  const priorO2 = oxygenLine(p);

  return (
    <Section
      title="Clinical snapshot"
      subtitle={
        <>Assembled from the record · vitals {v ? fmtAgo(v.recorded_at) : 'not recorded'}</>
      }
    >
      <div className="grid gap-4 lg:grid-cols-3">
        {/* ── Why admitted, and where things stand ── */}
        <div className="space-y-3 lg:col-span-1">
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Reason for admission</div>
            <div className="mt-0.5 text-[13px]">{data.reason_for_admission || 'Not documented'}</div>
          </div>
          {data.working_diagnosis && (
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Working diagnosis</div>
              <div className="mt-0.5 text-[13px]">{data.working_diagnosis}</div>
            </div>
          )}
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Active problems</div>
            {data.active_problems.length ? (
              <ul className="mt-1 space-y-1">
                {data.active_problems.map((pr, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-[13px]">
                    <span>{pr.diagnosis}</span>
                    {pr.status && <Badge variant={
                      pr.status === 'worsening' ? 'danger'
                        : pr.status === 'improving' ? 'success'
                        : pr.status === 'new' ? 'warn' : 'neutral'
                    }>{pr.status}</Badge>}
                    <SourceLink source={pr.source ?? null} onOpen={onOpen} label="↗" />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-0.5 text-[13px] text-ink-muted">None recorded</div>
            )}
          </div>
        </div>

        {/* ── Current condition ── */}
        <div className="space-y-3 lg:col-span-1">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
                Latest vitals {p && <span className="normal-case font-normal">· change vs ~12h earlier</span>}
              </div>
              <SourceLink source={v ? { type: 'vitals', id: v.id } : null} onOpen={onOpen} label="↗" />
            </div>
            {v ? (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {v.bp_systolic != null && (
                  <Metric label="BP" value={`${v.bp_systolic}/${v.bp_diastolic ?? '—'}`} />
                )}
                <VitalTrend label="Pulse" unit="" latest={v.pulse_rate} prior={p?.pulse_rate} invert />
                <VitalTrend label="RR" unit="" latest={v.respiratory_rate} prior={p?.respiratory_rate} invert />
                <VitalTrend label="SpO2" unit="%" latest={v.spo2} prior={p?.spo2} />
                <VitalTrend label="Temp" unit="°C" latest={v.temperature as number} prior={p?.temperature as number} invert />
                {v.pain_score != null && <Metric label="Pain" value={`${v.pain_score}/10`} />}
              </div>
            ) : (
              <div className="text-[13px] text-ink-muted">No vitals recorded for this admission.</div>
            )}
          </div>

          {o2 && (
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Oxygen</div>
              <div className="mt-0.5 text-[13px]">
                {o2}
                {priorO2 && priorO2 !== o2 && (
                  <span className="ml-1 text-[11px] text-ink-muted">(was {priorO2})</span>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Fluid balance (24h)</div>
            <div className="mt-0.5 text-[13px] tabular-nums">
              In {data.fluid_balance_24h.input} mL · Out {data.fluid_balance_24h.output} mL ·{' '}
              <span className="font-semibold">
                Net {data.fluid_balance_24h.net >= 0 ? '+' : ''}{data.fluid_balance_24h.net} mL
              </span>
            </div>
          </div>
        </div>

        {/* ── Treatment and what is outstanding ── */}
        <div className="space-y-3 lg:col-span-1">
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
              Active medication orders ({data.medication_orders.length})
            </div>
            {data.medication_orders.length ? (
              <ul className="mt-1 space-y-0.5">
                {data.medication_orders.slice(0, 5).map(o => (
                  <li key={o.id} className="flex items-center gap-1.5 text-[13px]">
                    <span className="truncate">
                      {o.drug_name}{o.strength ? ` ${o.strength}` : ''}
                      {o.route ? ` ${o.route}` : ''}{o.frequency_code ? ` ${o.frequency_code}` : ''}
                      {o.is_prn ? ' PRN' : ''}
                    </span>
                    {o.status === 'held' && <Badge variant="caution">held</Badge>}
                  </li>
                ))}
                {data.medication_orders.length > 5 && (
                  <li className="text-[11.5px] text-ink-muted">+{data.medication_orders.length - 5} more</li>
                )}
              </ul>
            ) : (
              <div className="mt-0.5 text-[13px] text-ink-muted">No active orders</div>
            )}
            {/* Orders are intentions. This line exists so nobody reads the
                list above as evidence that anything was administered. */}
            <div className="mt-1 text-[10.5px] text-ink-muted">
              Orders only — see the medication chart for what was administered.
            </div>
          </div>

          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Pending / needs attention</div>
            <ul className="mt-1 space-y-0.5 text-[13px]">
              {unreviewedCritical.map(i => (
                <li key={`c${i.id}`} className="flex items-center gap-1.5">
                  <Badge variant="danger">critical</Badge>
                  <span className="truncate">{i.investigation_name} — not reviewed</span>
                  <SourceLink source={i.source} onOpen={onOpen} label="↗" />
                </li>
              ))}
              {pendingResults.length > 0 && <li>{pendingResults.length} investigation result(s) pending</li>}
              {openConsults.length > 0 && (
                <li>{openConsults.length} open consult{openConsults.length > 1 ? 's' : ''} ({openConsults.map(c => c.specialty_name).filter(Boolean).join(', ') || 'specialist'})</li>
              )}
              {heldOrders.length > 0 && <li>{heldOrders.length} medication order(s) on hold</li>}
              {openCare.length > 0 && <li>{openCare.length} open care-plan activit{openCare.length > 1 ? 'ies' : 'y'}</li>}
              {data.assessment_status !== 'signed' && (
                <li className="text-warn-ink">
                  {data.assessment_status === 'draft' ? 'Admission assessment unsigned' : 'No admission assessment'}
                </li>
              )}
              {!unreviewedCritical.length && !pendingResults.length && !openConsults.length
                && !heldOrders.length && !openCare.length && data.assessment_status === 'signed' && (
                <li className="text-ink-muted">Nothing outstanding</li>
              )}
            </ul>
          </div>

          {data.last_progress_note && (
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Last physician note</div>
              <div className="mt-0.5 text-[13px]">
                {fmtAgo(data.last_progress_note.note_datetime)}
                {data.last_progress_note.author_name ? ` · ${data.last_progress_note.author_name}` : ''}
                {data.last_progress_note.status === 'draft' && <Badge variant="warn" className="ml-1.5">draft</Badge>}
              </div>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
